/**
 * Silent lead attribution. Everything here is best-effort: every read is
 * wrapped so a blocked storage API or odd cookie can never stop a form submit.
 * No location capture; the lead worker resolves that server-side.
 */
const VISIT_KEY = "bhg_attr_visit"; // sessionStorage: this visit
const FIRST_KEY = "bhg_attr_first"; // localStorage: first ever visit, never overwritten
const PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid", "fbclid", "msclkid"] as const;
const MAX = 500;

type Attr = Record<string, string>;

function cap(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t ? t.slice(0, MAX) : undefined;
}

function clean(o: Record<string, unknown>): Attr {
  const out: Attr = {};
  for (const [k, v] of Object.entries(o)) {
    const c = cap(v);
    if (c) out[k] = c;
  }
  return out;
}

/** Call once on the first page load of each visit. */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  let ref = "";
  let land = "";
  const q: Record<string, string | null> = {};
  try {
    ref = document.referrer || "";
    land = window.location.pathname + window.location.search;
    const sp = new URLSearchParams(window.location.search);
    for (const p of PARAMS) q[p] = sp.get(p);
  } catch {
    return;
  }
  try {
    if (!window.sessionStorage.getItem(VISIT_KEY)) {
      window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(clean({ ref, land, ...q })));
    }
  } catch {
    /* storage unavailable */
  }
  try {
    if (!window.localStorage.getItem(FIRST_KEY)) {
      window.localStorage.setItem(
        FIRST_KEY,
        JSON.stringify(
          clean({
            first_ref: ref,
            first_src: q.utm_source,
            first_med: q.utm_medium,
            first_cmp: q.utm_campaign,
            first_at: new Date().toISOString(),
          }),
        ),
      );
    }
  } catch {
    /* storage unavailable */
  }
}

function readJson(store: () => Storage, key: string): Attr {
  try {
    const raw = store().getItem(key);
    if (!raw) return {};
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? clean(v) : {};
  } catch {
    return {};
  }
}

function cookies(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (const part of document.cookie.split(";")) {
      const i = part.indexOf("=");
      if (i > 0) out[part.slice(0, i).trim()] = decodeURIComponent(part.slice(i + 1).trim());
    }
  } catch {
    /* ignore */
  }
  return out;
}

/** `_ga` = GA1.1.<rand>.<ts> → "<rand>.<ts>". Anything else → undefined. */
function clientId(c: Record<string, string>): string | undefined {
  const m = /^GA1\.\d+\.(\d+)\.(\d+)$/.exec(c["_ga"] ?? "");
  return m ? `${m[1]}.${m[2]}` : undefined;
}

/** Session id from the single `_ga_<ID>` cookie (GS2 `s` segment or GS1 third field). */
function sessionId(c: Record<string, string>): string | undefined {
  const keys = Object.keys(c).filter((k) => k.startsWith("_ga_"));
  if (keys.length !== 1) return undefined; // ambiguous → send nothing rather than guess
  const v = c[keys[0]!] ?? "";
  const gs2 = /^GS2\.\d+\.(.+)$/.exec(v);
  if (gs2) {
    const seg = gs2[1]!.split("$").find((s) => /^s\d+$/.test(s));
    return seg ? seg.slice(1) : undefined;
  }
  const gs1 = /^GS1\.\d+\.(\d+)\./.exec(v);
  return gs1 ? gs1[1] : undefined;
}

/** Attribution object for the lead POST. Returns only keys with values. */
export function getAttribution(): Attr {
  if (typeof window === "undefined") return {};
  let c: Record<string, string> = {};
  try {
    c = cookies();
  } catch {
    /* ignore */
  }
  let cid: string | undefined;
  let sid: string | undefined;
  try {
    cid = clientId(c);
    sid = sessionId(c);
  } catch {
    /* ignore */
  }
  let fbp: string | undefined;
  let fbc: string | undefined;
  try {
    fbp = c["_fbp"];
    fbc = c["_fbc"];
  } catch {
    /* ignore */
  }
  return {
    ...readJson(() => window.sessionStorage, VISIT_KEY),
    ...clean({ cid, sid, fbp, fbc }),
    ...readJson(() => window.localStorage, FIRST_KEY),
  };
}

/** One ID per submit attempt, shared by the lead POST (eventId) and the dataLayer (event_id). */
export function newLeadEventId(): string {
  return "lead_" + (crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "") : (Date.now().toString(36) + Math.random().toString(36).slice(2))).slice(0, 32);
}
